import { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCcw, Save, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const BAND_LABEL = { none: "Never block — warn only", critical: "Block critical only",
  high: "Block high and critical" };
const REGIME_LABEL = { IN: "India · Section 80G", GB: "United Kingdom · Gift Aid",
  US: "United States · 501(c)(3)" };

export const TenantPolicySettings = () => {
  const [meta, setMeta] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/admin/tenant-policy");
    setMeta(res.data);
    setValues(res.data.values);
  }, []);

  useEffect(() => { load().catch(() => setMeta(false)); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const res = await api.put("/admin/tenant-policy", { values });
      toast.success(`${Object.keys(res.data.changed).length} setting(s) updated for the whole tenant`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!meta) return <Loading label="Loading tenant policy" />;
  const groups = [...new Set(meta.fields.map((f) => f.group))];
  const dirty = JSON.stringify(values) !== JSON.stringify(meta.values);

  return (
    <Panel testId="panel-tenant-policy" kicker="Process-critical settings"
           title="Tenant policy — enforced on the server for everyone"
           actions={meta.editable && (
             <div className="flex gap-2">
               <Button size="sm" variant="outline" data-testid="policy-reset-button"
                       onClick={() => setValues(meta.defaults)}
                       className="h-8 rounded-full border-border text-[11px]">
                 <RotateCcw className="mr-1 h-3.5 w-3.5" /> Platform defaults
               </Button>
               <Button size="sm" onClick={save} disabled={busy === "save" || !dirty}
                       data-testid="policy-save-button"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <Save className="mr-1 h-3.5 w-3.5" />} Save policy
               </Button>
             </div>
           )}>
      {!meta.editable && (
        <div className="border-b border-border bg-muted/30 px-4 py-2.5 text-[11.5px] text-muted-foreground"
             data-testid="policy-readonly-notice">
          These settings change how the platform behaves for everyone, so only a tenant admin may edit
          them. Look-and-feel choices — theme, backdrop, dashboard widget order — stay yours to change.
        </div>
      )}
      <div className="divide-y divide-border">
        {groups.map((group) => (
          <div key={group} className="p-4" data-testid={`policy-group-${group.replace(/\W+/g, "-").toLowerCase()}`}>
            <div className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <SlidersHorizontal className="h-3 w-3 text-primary" /> {group}
            </div>
            <div className="space-y-3">
              {meta.fields.filter((f) => f.group === group).map((f) => (
                <div key={f.key} data-testid={`policy-row-${f.key}`}
                     className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-muted/20 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium text-foreground">{f.label}</div>
                    {f.help && <div className="text-[11px] text-muted-foreground">{f.help}</div>}
                  </div>
                  {f.type === "switch" && (
                    <Switch checked={Boolean(values[f.key])} disabled={!meta.editable}
                            data-testid={`policy-switch-${f.key}`}
                            onCheckedChange={(v) => setValues({ ...values, [f.key]: v })} />
                  )}
                  {f.type === "number" && (
                    <Input type="number" min={f.min} max={f.max} value={values[f.key]}
                           disabled={!meta.editable} data-testid={`policy-input-${f.key}`}
                           onChange={(e) => setValues({ ...values, [f.key]: Number(e.target.value) })}
                           className="w-24 bg-muted/40 font-mono text-[12px]" />
                  )}
                  {f.type === "select" && (
                    <Select value={String(values[f.key])} disabled={!meta.editable}
                            onValueChange={(v) => setValues({ ...values, [f.key]: v })}>
                      <SelectTrigger data-testid={`policy-select-${f.key}`}
                                     className="w-[220px] bg-muted/40 text-[12px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {f.options.map((o) => (
                          <SelectItem key={o} value={o}>
                            {BAND_LABEL[o] || REGIME_LABEL[o] || o}
                          </SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground"
           data-testid="policy-audit-line">
        {meta.updated_by
          ? `Last changed by ${meta.updated_by} · ${dateTime(meta.updated_at)}`
          : "Still on platform defaults — nothing has been overridden."}
      </div>
    </Panel>
  );
};
